/* Assigning a workout to a client's days — the rule, not the screen.
 *
 * This is the first thing the web app WRITES, and that changes what the pure layer owes. Reading
 * wrongly shows a coach a wrong number; writing wrongly puts training in front of an athlete, or
 * fails halfway through a Mon/Wed/Fri and leaves them with Monday only.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * THE WRITE ITSELF IS NOT OURS TO INVENT, AND THAT IS THE MOST IMPORTANT DECISION HERE.
 *
 * `assign_coach_workout(p_workout_id, p_client_id, p_scheduled_date, p_note)` already exists in
 * the database, is what the phone calls, and is **`SECURITY INVOKER`** — it runs under the
 * caller's own Row Level Security, so it can do nothing a coach could not do by hand. It clones
 * the workout into a client-scoped copy (V7-2's copy-on-assign) and inserts the assignment in one
 * statement.
 *
 * **So the web app calls the same function rather than reproducing its two writes.** A second
 * implementation of "assign" would be a second place where a clone can drift from its source —
 * and `docs/WEB-APP-PLAN.md` §1 chose "no server" precisely to avoid a second place where
 * authorisation is decided. The same reasoning applies one level down to a write.
 *
 * ⚠️ AND THE DATABASE, NOT THIS FILE, IS THE AUTHORITY ON DUPLICATES.
 * `coach_assignments_one_source_per_day_idx` is UNIQUE on
 * `(client_id, coalesce(root_workout_id, workout_id), scheduled_date)` — V7-2 re-keyed it on the
 * ROOT because copy-on-assign makes `workout_id` unique per assignment by construction, so a
 * `workout_id` key could never collide again. What `alreadyAssigned` below does is **predict**
 * that refusal so the coach is told before the attempt; it does not replace it. If the two ever
 * disagree the database wins, and `describeOutcome` has a case for exactly that.
 * ══════════════════════════════════════════════════════════════════════════════════════════
 */


/** An assignment that already exists, reduced to what a duplicate check needs. */
export type ExistingAssignment = {
  scheduledDateISO: string | null;
  /** `coalesce(root_workout_id, workout_id)` — the identity the unique index is keyed on. */
  sourceWorkoutId: string | null;
};

/** What will happen to one chosen date, decided before anything is written. */
export type PlannedWrite =
  | { dateISO: string; action: "assign" }
  | { dateISO: string; action: "alreadyAssigned" };

/**
 * Which of the chosen dates are new.
 *
 * ⚠️ THE KEY IS THE SOURCE WORKOUT, NOT THE WORKOUT. Every assignment points at its own private
 * copy, so comparing `workout_id` would find no duplicate ever and the coach would be told the
 * write is fine right up until the database refuses it. `coalesce(root_workout_id, workout_id)`
 * is the same expression the unique index uses — deliberately the same, so this predicts the
 * real refusal rather than a plausible one of its own.
 */
export function planWrites(
  dates: string[],
  sourceWorkoutId: string,
  existing: ExistingAssignment[],
): PlannedWrite[] {
  const taken = new Set(
    existing
      .filter((a) => a.sourceWorkoutId === sourceWorkoutId && a.scheduledDateISO !== null)
      .map((a) => a.scheduledDateISO as string),
  );
  // Sorted and de-duplicated: a coach can tap the same day twice, and two writes for one date
  // would make the second fail against the first — an error the coach caused by tapping.
  const unique = [...new Set(dates)].sort();
  return unique.map((d) =>
    taken.has(d) ? { dateISO: d, action: "alreadyAssigned" } : { dateISO: d, action: "assign" },
  );
}

/** The result of one attempted write. */
export type WriteOutcome =
  | { dateISO: string; kind: "assigned" }
  | { dateISO: string; kind: "alreadyAssigned" }
  | { dateISO: string; kind: "refused"; message: string };

/**
 * Turn the database's own words into something a coach can act on.
 *
 * ⚠️ ONLY THE DUPLICATE IS TRANSLATED, AND EVERY OTHER ERROR IS PASSED THROUGH UNTOUCHED.
 * A friendly blanket message — "could not assign, please try again" — is the shape this repo has
 * been bitten by over and over: `delete-account`'s tolerant `ignorable()` swallowed three
 * column-name typos and returned `{ok: true}` for work it had not done. A message we did not
 * write is the only evidence of a cause we did not anticipate.
 */
export function describeOutcome(dateISO: string, error: { message: string } | null): WriteOutcome {
  if (error === null) return { dateISO, kind: "assigned" };
  if (isDuplicateDayError(error.message)) return { dateISO, kind: "alreadyAssigned" };
  return { dateISO, kind: "refused", message: error.message };
}

/**
 * Is this Postgres telling us the one-per-day index refused the write?
 *
 * The unique index BY NAME. Matching on the name rather than on "duplicate key" prose, because
 * the prose is Postgres's and may be localised or reworded; the index name is ours.
 *
 * ⚠️ IT LIVES HERE, ALONE, BECAUSE TWO PATHS NOW ASK IT. Assigning hits the index on an INSERT
 * and rescheduling hits the SAME index on an UPDATE of `scheduled_date` — measured live in
 * `tools/b4-11-assignment-controls/00-rehearse.sql`, refused **23505** with this index named. A
 * second copy of the string in `assignmentEdit.ts` would drift the first time anybody renamed
 * the index, which V7-2 has already done once: `coach_assignments_one_per_day_idx` became
 * `coach_assignments_one_source_per_day_idx` when the key moved to the root workout.
 */
export function isDuplicateDayError(message: string): boolean {
  return message.includes(ONE_SOURCE_PER_DAY_INDEX);
}

/** The index's name, stated ONCE (harness ruler: exactly one web source file holds it) — the example's in-memory assign
 *  (`example/weekLibrary.ts`) refuses in its words by importing this, never by restating it. */
export const ONE_SOURCE_PER_DAY_INDEX = "coach_assignments_one_source_per_day_idx";

/**
 * One sentence for the whole action, because the coach performed ONE act.
 *
 * `PushDelivery`'s rule, stated for a screen instead of a notification: assigning Mon/Wed/Fri is
 * a single decision, so it earns a single sentence — and a per-day list of three toasts is how a
 * surface becomes noise. The counts are named separately because they need different responses:
 * a duplicate is nothing to do, a refusal is.
 */
export function summarise(outcomes: WriteOutcome[]): string {
  const assigned = outcomes.filter((o) => o.kind === "assigned").length;
  const dupes = outcomes.filter((o) => o.kind === "alreadyAssigned").length;
  const refused = outcomes.filter((o) => o.kind === "refused");
  const parts: string[] = [];
  if (assigned > 0) parts.push(`${assigned} day${assigned === 1 ? "" : "s"} assigned`);
  if (dupes > 0) parts.push(`${dupes} already had this workout`);
  if (refused.length > 0) {
    parts.push(`${refused.length} refused: ${refused.map((r) => r.message).join("; ")}`);
  }
  return parts.length === 0 ? "Nothing to assign." : parts.join(" · ") + ".";
}
